import { io, Socket } from "socket.io-client"
import { ApiError } from "@/shared/api"

type WebSocketEvent =
  | "chat:rooms:list"
  | "chat:room:create"
  | "chat:room:update"
  | "chat:room:delete"
  | "chat:room:leave"
  | "chat:room:join"
  | "chat:messages:list"
  | "chat:message:send"
  | "chat:message:update"
  | "chat:message:delete"
  | "chat:message:file:delete"

type WebSocketServerEvent =
  | "chat:message:created"
  | "chat:message:updated"
  | "chat:message:deleted"
  | "chat:room:updated"
  | "chat:room:deleted"
  | "files:file:created"
  | "files:file:updated"
  | "files:file:deleted"
  | "files:folder:created"
  | "files:folder:updated"
  | "files:folder:deleted"
  | "files:document:created"
  | "files:document:updated"
  | "files:document:deleted"
  | "system:package-connection"
  | "notification:created"
  | iSharedOperatingRooms.RealtimeEventName

type WebSocketServerEventHandler<TResult> = (result: TResult) => void

export class WebSocketClient {
  private readonly socket: Socket

  constructor(baseUrl: string) {
    this.socket = io(this.normalizeBaseUrl(baseUrl), {
      withCredentials: true,
      autoConnect: false,
      transports: ["websocket"],
      path: "/v1/connection"
    })
  }

  connect(): void {
    if (!this.socket.connected) {
      this.socket.connect()
    }
  }

  disconnect(): void {
    this.socket.disconnect()
  }

  on<TResult>(eventName: WebSocketServerEvent, handler: WebSocketServerEventHandler<TResult>): () => void {
    const socketHandler = (payload: unknown) => {
      const envelope = this.parseEnvelope<TResult>(payload)
      if (envelope.ok) handler(envelope.result)
    }

    this.socket.on(eventName, socketHandler)
    return () => this.off(eventName, socketHandler)
  }

  off(eventName: WebSocketServerEvent, handler?: WebSocketServerEventHandler<unknown>): void {
    if (handler) {
      this.socket.off(eventName, handler)
      return
    }

    this.socket.off(eventName)
  }

  emit<TResult, TPayload = never>(eventName: WebSocketEvent, payload?: TPayload): Promise<TResult> {
    this.connect()

    return new Promise((resolve, reject) => {
      this.socket.emit(eventName, payload, (response: unknown) => {
        try {
          const envelope = this.parseEnvelope<TResult>(response)
          if (envelope.ok) {
            resolve(envelope.result)
            return
          }

          reject(new ApiError(envelope.error.code, envelope.error.message, 0))
        } catch (error) {
          reject(error)
        }
      })
    })
  }

  private parseEnvelope<TResult>(payload: unknown): iSharedApi.ResponseEnvelope<TResult> {
    if (!this.isEnvelope<TResult>(payload)) {
      throw new ApiError("API_INVALID_RESPONSE", "Некорректный формат WebSocket ответа", 0)
    }

    return payload
  }

  private isEnvelope<TResult>(payload: unknown): payload is iSharedApi.ResponseEnvelope<TResult> {
    if (!this.isRecord(payload)) return false
    if (typeof payload.ok !== "boolean") return false
    if (payload.ok) return "result" in payload && payload.error === null

    return payload.result === null && this.isRecord(payload.error) && typeof payload.error.code === "string" && typeof payload.error.message === "string"
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
  }

  private normalizeBaseUrl(baseUrl: string): string {
    if (!baseUrl) {
      throw new ApiError("API_CONFIG_ERROR", "Не задан базовый URL WebSocket", 0)
    }

    return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
  }
}
